import { Link } from '@tanstack/react-router'
import { Edit, Eye, LogOut, MoreHorizontal, UserCheck, UserX } from 'lucide-react'
import { useState } from 'react'

import { useDisableEmployee, useEnableEmployee, type Employee, type EmployeeStatus } from '@/hooks'
import { cn } from '@/lib/utils'

interface EmployeeQuickActionsProps {
  employee: Employee
  onEdit?: () => void
  onStartOffboarding?: () => void
}

const STATUS_CONFIG: Record<EmployeeStatus, { label: string; color: string }> = {
  active: { label: '在职', color: 'bg-green-100 text-green-800' },
  on_leave: { label: '休假中', color: 'bg-yellow-100 text-yellow-800' },
  resigned: { label: '已离职', color: 'bg-gray-100 text-gray-800' },
}

export function EmployeeStatusBadge({ status }: { status: EmployeeStatus }) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.active
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', config.color)}>
      {config.label}
    </span>
  )
}

export function EmployeeQuickActions({ employee, onEdit, onStartOffboarding }: EmployeeQuickActionsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const disableMutation = useDisableEmployee()
  const enableMutation = useEnableEmployee()

  const handleDisable = () => {
    disableMutation.mutate(employee.id)
    setIsOpen(false)
  }

  const handleEnable = () => {
    enableMutation.mutate(employee.id)
    setIsOpen(false)
  }

  const handleOffboarding = () => {
    onStartOffboarding?.()
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded p-1 hover:bg-muted"
        aria-label="操作菜单"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Menu */}
          <div className="absolute right-0 z-20 mt-1 w-40 rounded-md border bg-background shadow-lg">
            {/* View Profile */}
            <Link
              to="/hr/employees/$employeeId"
              params={{ employeeId: employee.id }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted"
              onClick={() => setIsOpen(false)}
            >
              <Eye className="h-4 w-4" />
              查看档案
            </Link>

            {/* Edit */}
            {onEdit && (
              <button
                onClick={() => {
                  onEdit()
                  setIsOpen(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted"
              >
                <Edit className="h-4 w-4" />
                编辑
              </button>
            )}

            {/* Disable/Enable */}
            {employee.status === 'active' && (
              <button
                onClick={handleDisable}
                disabled={disableMutation.isPending}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:bg-muted"
              >
                <UserX className="h-4 w-4" />
                禁用账号
              </button>
            )}

            {employee.status === 'resigned' && (
              <button
                onClick={handleEnable}
                disabled={enableMutation.isPending}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-muted"
              >
                <UserCheck className="h-4 w-4" />
                重新激活
              </button>
            )}

            {/* Start Offboarding */}
            {employee.status === 'active' && onStartOffboarding && (
              <button
                onClick={handleOffboarding}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-muted"
              >
                <LogOut className="h-4 w-4" />
                开始离职
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
